import React, { useState } from "react";
import { SectionHeader } from "@/components/ui/section-header";
import { GlassCard } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCw,
  Trash,
} from "lucide-react";
import { toast } from "sonner";
import { useAppBookOrgUnits, useInvalidateAppBook } from "@/hooks/useAppBook";
import { deleteOrgUnit } from "@/services/appBookApi";
import type { AppBookOrgUnit } from "@/types/appbook";
import { AddAppBookDialog } from "./AddAppBookDialog";
import { AppBookAssetsTable } from "./AppBookAssetsTable";

function formatDate(epochSeconds?: number): string {
  if (!epochSeconds) return "—";
  return new Date(epochSeconds * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ExpiryBadge({ epochSeconds }: { epochSeconds: number }) {
  const expiry = new Date(epochSeconds * 1000);
  const daysLeft = Math.ceil((expiry.getTime() - Date.now()) / 86400000);

  if (daysLeft < 0) {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        Expired {Math.abs(daysLeft)}d ago
      </Badge>
    );
  }
  if (daysLeft <= 30) {
    return (
      <Badge className="gap-1 border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
        <AlertTriangle className="h-3 w-3" />
        Expires in {daysLeft}d
      </Badge>
    );
  }
  if (daysLeft <= 90) {
    return (
      <Badge className="gap-1 border-transparent bg-warning/10 text-warning">
        <Clock className="h-3 w-3" />
        Expires {formatDate(epochSeconds)}
      </Badge>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
      <CheckCircle className="h-3 w-3" />
      Valid until {formatDate(epochSeconds)}
    </span>
  );
}

export function AppBookSection() {
  const { data: orgUnits = [], isLoading, error, refetch } = useAppBookOrgUnits();
  const { invalidateOrgUnits } = useInvalidateAppBook();
  const [addOpen, setAddOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<AppBookOrgUnit | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteOrgUnit(deleteTarget.id);
      toast.success("Organisation unit deleted");
      setDeleteTarget(null);
      invalidateOrgUnits();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to delete organisation unit";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="space-y-4 pt-6 border-t">
      <SectionHeader
        title="Apple Apps & Books"
        description="Manage VPP content tokens and view licensed apps per organisation unit"
        buttonLabel="Add Organisation Unit"
        onButtonClick={() => setAddOpen(true)}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading Apps &amp; Books accounts…</span>
        </div>
      ) : error ? (
        <div className="border-2 border-dashed rounded-lg p-8 text-center">
          <p className="text-sm text-destructive mb-2">Failed to load Apps &amp; Books accounts</p>
          <p className="text-xs text-muted-foreground mb-4">
            {error instanceof Error ? error.message : "Unknown error"}
          </p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      ) : orgUnits.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <h3 className="text-lg font-semibold mb-1">No Apps &amp; Books Accounts</h3>
          <p className="text-sm text-muted-foreground">
            Upload a VPP content token from Apple Business Manager to get started.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {orgUnits.map((unit) => {
            const isOpen = !!expanded[unit.id];
            return (
              <GlassCard key={unit.id} variant="subtle" hover={false} className="overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <button
                      type="button"
                      onClick={() => toggle(unit.id)}
                      className="flex items-start gap-3 text-left flex-1 min-w-0"
                      aria-expanded={isOpen}
                    >
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-base truncate">
                            {unit.organisationUnit}
                          </h4>
                          <span className="text-sm text-muted-foreground">· {unit.orgName}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <ExpiryBadge epochSeconds={unit.expDate} />
                          <span>Created {formatDate(unit.createdAt)}</span>
                          <span>Updated {formatDate(unit.updatedAt)}</span>
                        </div>
                      </div>
                    </button>
                    <ActionsDropdown
                      actions={[
                        {
                          label: "Delete",
                          icon: Trash,
                          onClick: () => setDeleteTarget(unit),
                          destructive: true,
                        },
                      ]}
                    />
                  </div>

                  {isOpen && (
                    <div className="mt-4 pl-7">
                      <AppBookAssetsTable orgUnitId={unit.id} />
                    </div>
                  )}
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      <AddAppBookDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSuccess={invalidateOrgUnits}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organisation Unit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.organisationUnit}&quot;? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
